import { useParams, Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import * as React from "react";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { formatDateSafe, parseDateLocal } from "@/lib/utils";
import { trackClientView } from "@/components/dashboard/RecentClients";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronRight, ChevronsUpDown, Check } from "lucide-react";
import { Client, Employee, ContactInteraction, Plan } from "@shared/schema";
import { AddPlanDialog } from "@/components/ui/plans/AddPlanDialog";
import ClientProfile from "./ClientDetailComponents/ClientProfile";
import ClientEmployees from "./ClientDetailComponents/ClientEmployees";
import ClientHeader from "./ClientDetailComponents/ClientHeader";
import EditClientDialog from "./ClientDetailComponents/EditClientDialog";
import DocumentsTab from "./ClientDetailComponents/DocumentsTab";
import ComplianceTab from "./ClientDetailComponents/ComplianceTab";
import OrganizationTab from "./ClientDetailComponents/OrganizationTab";
import BenefitsTab from "./ClientDetailComponents/BenefitsTab";
import HRTab from "./ClientDetailComponents/HRTab";
import LoginManagement from "./ClientDetailComponents/LoginManagement";
import { EmployeesSectionToggle } from "@/components/employees/EmployeesSectionToggle";
import ACAModule from "@/pages/employer/aca/ACAModule";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useClientPlans } from "@/hooks/useClientPlans";
import { useClientMonthlyPremium } from "@/hooks/useClientMonthlyPremium";
import { upcomingPlansSummary } from "@/lib/upcomingPlans";
import { getEmployeeHeadcount } from "@/lib/employeeHeadcount";
import { useMemo } from "react";

const CLIENT_TABS = [
  { value: "profile", label: "Profile" },
  { value: "employees", label: "Employees" },
  { value: "benefits", label: "Benefits" },
  { value: "hr", label: "HR" },
  { value: "documents", label: "Documents" },
  { value: "compliance", label: "Compliance" },
  { value: "aca", label: "ACA" },
  { value: "organization", label: "Org." },
] as const;

// Accepted `?tab=` values. "logins" is a legacy alias that redirects to the
// Employees tab with the Login Management sub-view (`?tab=employees&view=logins`).
const CLIENT_TAB_VALUES = [...CLIENT_TABS.map((tab) => tab.value), "logins"] as const;
const EMPLOYEE_VIEWS = ["employees", "logins"] as const;

// Breadcrumb client name -> searchable dropdown to jump between clients.
function ClientNameSwitcher({
  clients,
  currentId,
  currentName,
  onSelect,
}: {
  clients: Client[];
  currentId: number;
  currentName: string;
  onSelect: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className="flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300"
        >
          {currentName}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search clients..." className="h-9" />
          <CommandList>
            <CommandEmpty>No clients found.</CommandEmpty>
            <CommandGroup>
              {clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.id}`}
                  onSelect={() => {
                    onSelect(c.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 ${c.id === currentId ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const ClientDetail = () => {
  const { id } = useParams();
  const clientIdNum = id ? parseInt(id, 10) : undefined;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Track this client view for "Recent Clients" dashboard widget
  useEffect(() => {
    if (clientIdNum) {
      trackClientView(clientIdNum);
    }
  }, [clientIdNum]);

  // URL-based tab persistence using nuqs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(CLIENT_TAB_VALUES).withDefault("profile")
  );
  const [employeesView, setEmployeesView] = useQueryState(
    "view",
    parseAsStringLiteral(EMPLOYEE_VIEWS).withDefault("employees")
  );
  // Legacy deep link: ?tab=logins → Employees tab, Login Management view
  useEffect(() => {
    if (activeTab === "logins") {
      setActiveTab("employees");
      setEmployeesView("logins");
    }
  }, [activeTab]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Use shared date formatter that safely handles YYYY-MM-DD and ISO forms

  // DEV-310: this used to overlay two localStorage caches onto the live
  // plan - `_customizations` (no writer anywhere in the codebase) and
  // `_rates`'s `.effectiveDate` field (has a writer, PlanRates.tsx, but
  // none of its 3 save paths ever include effectiveDate in what they
  // write). Both branches were dead reads that could only ever return
  // stale data. Reduced to a passthrough; kept as a function since it's
  // passed as a prop into shared BenefitsOverview/AvailablePlans components.
  const getEffectivePlan = (basePlan: Plan): Plan => basePlan;

  // Remove plan from this client (do NOT delete global plan)
  const deletePlanMutation = useMutation({
    mutationFn: async (planOrClientPlanId: number | { planId: number; clientPlanId: number | null }) => {
      // Handle both formats for backwards compatibility
      const clientPlanId = typeof planOrClientPlanId === 'object'
        ? planOrClientPlanId.clientPlanId
        : planOrClientPlanId;
      if (!clientPlanId) {
        throw new Error("Cannot remove plan without client-plan association");
      }
      return apiRequest("DELETE", `/api/client-plans/${clientPlanId}`);
    },
    onSuccess: (_data, clientPlanId: number) => {
      const clientIdNum = Number(id);

      // Optimistically remove from cache so UI updates immediately.
      const removeClientPlanId = (prev: any) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((cp: any) => Number(cp?.id) !== Number(clientPlanId));
      };

      queryClient.setQueryData(["/api/client-plans", id], removeClientPlanId);
      if (Number.isFinite(clientIdNum)) {
        queryClient.setQueryData(
          ["/api/client-plans", clientIdNum],
          removeClientPlanId
        );
      }

      // IMPORTANT: useClientPlans() caches under a numeric clientId key
      // so invalidating the string id key won't update the Benefits tab.
      if (Number.isFinite(clientIdNum)) {
        queryClient.invalidateQueries({ queryKey: ["/api/client-plans", clientIdNum] });
        queryClient.refetchQueries({ queryKey: ["/api/client-plans", clientIdNum] });
      }

      // Keep any other callers that used the string id key in sync.
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans/count", id] });
      toast({
        title: "Success",
        description: "Plan removed from client",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove plan",
        variant: "destructive",
      });
    },
  });

  //Client fetch
  const { data: client, isLoading: clientLoading } = useQuery<Client | null>({
    queryKey: ["/api/clients", id],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${id}`);
      // DEV-316: a 403/404 body is an error object, not a client. Treating it
      // as one rendered the page shell (premium, employees, plans) for a client
      // outside the broker's agency with the name stuck on "Loading...".
      if (res.status === 403 || res.status === 404) return null;
      if (!res.ok) throw new Error(`Failed to load client (${res.status})`);
      return res.json();
    },
    enabled: !!id,
  });

  // All clients for the breadcrumb switcher
  const { data: allClients = [] } = useQuery<Client[]>({ queryKey: ["/api/clients"] });
  const sortedClients = useMemo(
    () => [...allClients].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [allClients]
  );

  const navigateToClient = (targetId: number) => {
    setLocation(`/clients/${targetId}?tab=${activeTab}`);
  };

  const { data: employees = [], isLoading: employeesLoading } = useQuery<
    Employee[]
  >({
    queryKey: ["/api/clients", id, "employees"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${id}/employees`);
      return res.json();
    },
    enabled: !!id,
  });

  const employeesList = Array.isArray(employees) ? employees : [];

  // Same headcount the HR / Compliance / Open Enrollment tabs show (DEV-329).
  const employeeHeadcount = getEmployeeHeadcount(employeesList);

  // Server-backed count of active plans for this client
  const { data: activePlansCountFromServer } = useQuery<{ count: number }>({
    queryKey: ["/api/client-plans/count", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await fetch(`/api/client-plans/count?clientId=${id}`);
      if (!res.ok) return { count: 0 };
      return res.json();
    },
  });

  const { total: monthlyPremiumTotal, isLoading: monthlyPremiumLoading } =
    useClientMonthlyPremium(clientIdNum);

  const {
    data: contactInteractions = [],
    isLoading: contactInteractionsLoading,
  } = useQuery<ContactInteraction[]>({
    queryKey: ["/api/clients", id, "contact-interactions"],
    queryFn: () =>
      fetch(`/api/clients/${id}/contact-interactions`).then((res) =>
        res.json()
      ),
    enabled: !!id,
  });

  // Fetch client-specific plan associations (needed for renewalDate and status)
  const { data: clientPlans = [] } = useQuery({
    queryKey: ["/api/client-plans", id],
    queryFn: async () => {
      const res = await fetch(`/api/client-plans?clientId=${id}`);
      if (!res.ok) return [] as any[];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id,
  });

  // Use shared hook for core plan merging
  const { plans: mergedPlans, planYears: mergedPlanYears, allPlans, isLoading: plansLoading } = useClientPlans(clientIdNum);

  // Enhance merged plans with additional fields from clientPlans (renewalDate, status)
  const withLinkFields = (merged: any[]) => {
    if (!Array.isArray(merged) || !Array.isArray(clientPlans)) return [];

    // Create a map of clientPlanId -> clientPlan for quick lookup
    const clientPlanMap = new Map(
      (clientPlans as any[]).map((cp: any) => [cp.id, cp])
    );

    return merged.map((plan: any) => {
      const cp = clientPlanMap.get(plan.clientPlanId);
      if (!cp) return plan;

      return {
        ...plan,
        renewalDate: (cp as any).renewalDate ?? (plan as any).renewalDate,
        status:
          (cp as any).status ??
          ((plan as any).isActive === false ? "inactive" : "active"),
      } as any;
    });
  };
  // One entry per plan (the plan year in force) — pickers, enrollments, totals.
  const plansData = useMemo(() => withLinkFields(mergedPlans), [mergedPlans, clientPlans]);
  // Every plan year, incl. an upcoming renewal year — the Available Plans list.
  const planYearsData = useMemo(() => withLinkFields(mergedPlanYears), [mergedPlanYears, clientPlans]);
  // Plans linked but not yet in effect — the header shows them beside the
  // server's active count (same source Benefits > Overview summarizes).
  const upcomingPlansCount = useMemo(() => upcomingPlansSummary(plansData).count, [plansData]);

  // Fetch all plan contributions for calculating accurate premium splits
  const { data: allPlanContributions = [] } = useQuery({
    queryKey: ["/api/plan-contributions/all"],
    queryFn: () => fetch("/api/plan-contributions").then((res) => res.json()),
    enabled: !!plansData?.length,
  });

  // Fetch plan rates for accurate premium calculations
  const { data: planRates = [] } = useQuery({
    queryKey: ["/api/simple-plan-rates"],
    queryFn: () => fetch("/api/simple-plan-rates").then((res) => res.json()),
    enabled: !!plansData?.length,
  });

  // Get actual plan rate for a coverage tier
  const getPlanRate = (planId: number, coverageTier: string) => {
    const rates = Array.isArray(planRates) ? planRates : [];

    // Map display names to database values
    const tierMap: { [key: string]: string } = {
      "Employee Only": "employee",
      "Employee + Spouse": "employeeSpouse",
      "Employee + Child(ren)": "employeeChild",
      "Employee + Children": "employeeChild",
      Family: "employeeFamily",
    };

    const dbTierName = tierMap[coverageTier] || coverageTier.toLowerCase();
    const rate = rates.find(
      (r: any) => r.planId === planId && r.rateStructure === dbTierName
    );
    return rate ? parseFloat(rate.premium) : 0;
  };

  // Calculate employer/employee contributions based on plan contribution settings
  const calculateContributions = (
    planId: number,
    coverageTier: string,
    totalPremium: number
  ) => {
    const contributions = Array.isArray(allPlanContributions)
      ? allPlanContributions
      : [];
    const planContributions = contributions.find(
      (contrib: any) => contrib.planId === planId
    );

    if (!planContributions) {
      // Fallback to basic calculation if no contribution settings found
      return {
        employerContribution: totalPremium * 0.8,
        employeeContribution: totalPremium * 0.2,
      };
    }

    if (
      planContributions.contributionStructure === "base_plan" &&
      planContributions.basePlanId
    ) {
      // Use base plan contributions if linked
      const basePlanContributions = contributions.find(
        (contrib: any) => contrib.planId === planContributions.basePlanId
      );
      if (basePlanContributions) {
        return calculateContributions(
          planContributions.basePlanId,
          coverageTier,
          totalPremium
        );
      }
    }

    let employerContribution = 0;
    let employeeContribution = totalPremium;

    // Determine which tier to use based on coverage
    let tierType = "employee";
    if (coverageTier.includes("Spouse") && coverageTier.includes("Child")) {
      tierType = "threeChildren"; // Family coverage
    } else if (coverageTier.includes("Spouse")) {
      tierType = "spouse";
    } else if (coverageTier.includes("Child")) {
      tierType = "oneChild"; // Simplified for demo
    }

    // Get contribution settings for the tier
    const contributionType =
      planContributions[`${tierType}ContributionType`] || "percentage";
    const contributionAmount = parseFloat(
      planContributions[`${tierType}Contribution`] || "0"
    );
    const maxAmount = parseFloat(
      planContributions[`${tierType}MaxAmount`] || "0"
    );

    if (contributionType === "percentage") {
      employerContribution = totalPremium * (contributionAmount / 100);
      if (maxAmount > 0) {
        employerContribution = Math.min(employerContribution, maxAmount);
      }
    } else if (contributionType === "fixed_amount") {
      employerContribution = Math.min(contributionAmount, totalPremium);
    }

    employeeContribution = totalPremium - employerContribution;

    return {
      employerContribution: Math.max(0, employerContribution),
      employeeContribution: Math.max(0, employeeContribution),
    };
  };

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12" data-testid="client-no-access">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Client not found or you don't have access
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          This client doesn't exist or belongs to another agency.
        </p>
        <Link to="/clients">
          <Button>Back to Clients</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/clients" className="hover:text-gray-700 dark:hover:text-gray-300">
          Clients
        </Link>
        <ChevronRight className="h-4 w-4" />
        <ClientNameSwitcher
          clients={sortedClients}
          currentId={client.id}
          currentName={client.name}
          onSelect={navigateToClient}
        />
      </nav>

      {/* Header */}
      <ClientHeader
        client={client}
        employeeHeadcount={employeeHeadcount}
        activePlansCount={activePlansCountFromServer?.count ?? 0}
        upcomingPlansCount={upcomingPlansCount}
        monthlyPremiumTotal={monthlyPremiumTotal}
        monthlyPremiumLoading={monthlyPremiumLoading}
        onEditClick={() => setIsEditDialogOpen(true)}
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as typeof activeTab)}
        className="space-y-6"
      >
        {/* One row, full page width — each tab shares space equally (flex-1).
            min-w-0 lets cramped labels truncate instead of pushing the row past
            the container. */}
        <TabsList className="flex w-full gap-1 h-auto bg-[#eff6fe] dark:bg-gray-800">
          {CLIENT_TABS.map((tab) => {
            return <TabsTrigger key={tab.value} value={tab.value} className="flex-1 min-w-0 truncate bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">{tab.label}</TabsTrigger>
          })}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          {(() => {
            return (
              <ClientProfile
                client={client}
                employees={employeesList}
                contactInteractions={contactInteractions}
                contactInteractionsLoading={contactInteractionsLoading}
              />
            );
          })()}
        </TabsContent>

        <TabsContent value="employees" className="space-y-4">
          <EmployeesSectionToggle value={employeesView} onChange={setEmployeesView} />
          {employeesView === "logins" ? (
            <LoginManagement clientId={clientIdNum as number} />
          ) : (
            <ClientEmployees
              client={client}
              employees={employeesList}
              employeesLoading={employeesLoading}
              clientIdParam={id!}
            />
          )}
        </TabsContent>

        <TabsContent value="benefits" className="space-y-6">
          <BenefitsTab
            plansData={plansData}
            planYearsData={planYearsData}
            employees={employeesList}
            clientId={id!}
            plansLoading={plansLoading}
            getEffectivePlan={getEffectivePlan}
            formatDateSafe={formatDateSafe}
            getPlanRate={getPlanRate}
            calculateContributions={calculateContributions}
            deletePlanMutation={deletePlanMutation}
            setLocation={setLocation}
          />
        </TabsContent>

        <TabsContent value="hr" className="space-y-6">
          <HRTab
            clientId={clientIdNum as number}
            employees={employeesList}
            employeesLoading={employeesLoading}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <DocumentsTab
            clientName={client?.name || ""}
            clientId={clientIdNum as number}
          />
        </TabsContent>

        <TabsContent value="compliance" className="space-y-6">
          <ComplianceTab
            employees={employeesList}
            client={client}
            clientId={clientIdNum as number}
          />
        </TabsContent>

        <TabsContent value="aca" className="space-y-6">
          <ACAModule clientId={clientIdNum} />
        </TabsContent>

        <TabsContent value="organization" className="space-y-6">
          <OrganizationTab clientId={clientIdNum as number} />
        </TabsContent>
      </Tabs>

      {/* Edit Client Dialog */}
      <EditClientDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        client={client}
        employees={employeesList}
        clientId={id!}
      />
    </div>
  );
};

export default ClientDetail;
